import React, { useState } from "react";
import axios from "axios";
import { Send, Sparkles, AlertCircle, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * PushBlastPanel
 *
 * Admin-only. POSTs to `/api/push/broadcast` which fans out to every
 * device that has registered an Expo push token via the mobile app.
 *
 * The mobile app also lands new specials in the Notification Center on
 * iOS + system tray on Android with the accent gold icon colour.
 */
const TEMPLATES = [
  {
    key: "friday-special",
    title: "🍲 This Friday only",
    body: "Mama's Torborgee & fufu is back this Friday — first pot's ready at 11:30. Skip the queue and order ahead.",
  },
  {
    key: "kids-eat-cheap",
    title: "👨‍👩‍👧 Kids Combo — $10 flat",
    body: "Kids Meal + Kids Juice Popper caps at $10 in-app this week. Perfect for the after-school hunger.",
  },
  {
    key: "family-feast",
    title: "🎉 Family Feast Bundle",
    body: "Family Feast + 4 drinks = $8 off automatically. Feeds the whole crew for less than $50.",
  },
];

export default function PushBlastPanel() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const send = async () => {
    if (!title.trim() || !body.trim()) {
      setError("Add both a title and a body before firing the blast.");
      return;
    }
    setBusy(true);
    setResult(null);
    setError(null);
    try {
      const { data } = await axios.post(
        `${API}/api/push/broadcast`,
        { title: title.trim(), body: body.trim() },
        { withCredentials: true, headers: withCsrf() }
      );
      setResult(data);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Broadcast failed — please try again.";
      setError(String(detail));
    } finally {
      setBusy(false);
    }
  };

  function withCsrf() {
    if (typeof document === "undefined") return {};
    const m = document.cookie.match(/(?:^|; )mm_csrf=([^;]+)/);
    return m ? { "X-CSRF-Token": decodeURIComponent(m[1]) } : {};
  }

  const applyTemplate = (t) => {
    setTitle(t.title);
    setBody(t.body);
    setResult(null);
    setError(null);
  };

  return (
    <div className="max-w-3xl mx-auto py-8 px-6" data-testid="push-blast-panel">
      <div className="flex items-center gap-3 mb-2">
        <Send size={22} className="text-[var(--mm-accent)]" />
        <h2 className="text-2xl font-serif italic text-[var(--mm-cream)]">Weekly special blast</h2>
      </div>
      <p className="text-sm text-[var(--mm-cream)]/70 mb-8 max-w-xl">
        Push a notification to every phone with the mobile app installed. Use it for tonight&apos;s
        special, opening-hour changes, or when the pot&apos;s just come off the heat.
      </p>

      {/* Templates */}
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={14} className="text-[var(--mm-accent)]" />
        <span className="text-xs tracking-[0.24em] uppercase text-[var(--mm-accent)]">
          Quick templates
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
        {TEMPLATES.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => applyTemplate(t)}
            data-testid={`template-${t.key}`}
            className="text-left border border-[var(--mm-cream)]/15 hover:border-[var(--mm-accent)] bg-[var(--mm-charcoal)]/60 px-3 py-3 transition-colors"
          >
            <p className="text-xs text-[var(--mm-cream)] font-medium mb-1 line-clamp-1">{t.title}</p>
            <p className="text-[11px] text-[var(--mm-cream)]/55 line-clamp-2">{t.body}</p>
          </button>
        ))}
      </div>

      <label className="block text-xs tracking-[0.24em] uppercase text-[var(--mm-accent)] mb-2">
        Title
      </label>
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength={64}
        data-testid="push-title-input"
        placeholder="Something short and punchy…"
        className="w-full bg-[var(--mm-charcoal)]/60 border border-[var(--mm-cream)]/15 focus:border-[var(--mm-accent)] outline-none px-4 py-3 text-[var(--mm-cream)] mb-4"
      />

      <label className="block text-xs tracking-[0.24em] uppercase text-[var(--mm-accent)] mb-2">
        Body
      </label>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        maxLength={220}
        rows={4}
        data-testid="push-body-input"
        placeholder="Tell the diners what's cooking and why they should hurry over."
        className="w-full bg-[var(--mm-charcoal)]/60 border border-[var(--mm-cream)]/15 focus:border-[var(--mm-accent)] outline-none px-4 py-3 text-[var(--mm-cream)] mb-1 resize-none"
      />
      <p className="text-[11px] text-[var(--mm-cream)]/45 mb-6 text-right">
        {body.length} / 220
      </p>

      {/* Live preview — mimics an iOS push banner */}
      <div className="mb-6 border border-[var(--mm-cream)]/15 bg-[var(--mm-cream)]/95 rounded-lg px-4 py-3 text-[var(--mm-charcoal)] max-w-md">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-[var(--mm-charcoal)] flex items-center justify-center shrink-0">
            <img src="/media/logo-300.png" alt="" className="w-7 h-7 rounded" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold leading-tight">
              {title || "MAMAMINA'S SOULFEED"}
            </p>
            <p className="text-[12px] leading-snug mt-0.5">
              {body || "Your push preview will appear here as you type."}
            </p>
          </div>
          <p className="text-[10px] text-[var(--mm-charcoal)]/50 shrink-0">now</p>
        </div>
      </div>

      {error && (
        <div className="border border-red-500/50 bg-red-500/10 px-4 py-3 text-sm text-red-300 mb-4 flex items-start gap-2">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {result && (
        <div className="border border-[var(--mm-accent)] bg-[var(--mm-accent)]/10 px-4 py-3 text-sm text-[var(--mm-cream)] mb-4 flex items-start gap-2" data-testid="push-result">
          <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-[var(--mm-accent)]" />
          <span>
            Blast fired: <b>{result.sent}</b> delivered ·{" "}
            <b>{result.errors}</b> failed of{" "}
            <b>{result.tokens_targeted}</b> registered device{result.tokens_targeted === 1 ? "" : "s"}.
          </span>
        </div>
      )}

      <button
        type="button"
        onClick={send}
        disabled={busy || !title.trim() || !body.trim()}
        data-testid="push-send-btn"
        className="bg-[var(--mm-accent)] text-[var(--mm-black)] px-6 py-3 text-xs tracking-[0.24em] uppercase font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity flex items-center gap-2"
      >
        <Send size={13} />
        {busy ? "Sending…" : "Fire the blast"}
      </button>
    </div>
  );
}
